import axios from 'axios';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import api from '../../api/api';
import { HUBBLE_APP_SECRET, HUBBLE_CLIENT_ID, HUBBLE_SDK_URL } from '../../constants/config';
import { trackData } from '../../utils/trackers';
import './_embeddedApps.scss';

/**
 * Hosts the Hubble (gift cards) web SDK in an iframe that outlives the screen that shows it.
 *
 * Once the post-login data is in and the user is eligible, Hubble is authenticated and loaded in the
 * background. Hubble's SSO tokens are single-use and live for 60s, so a fresh one is kept on hand (refetched
 * shortly before it expires) for whenever the SDK has to be (re)loaded: after an error, a close, or a retry. The Gift Cards screen (pages/Hubble) only renders a slot; the iframe is positioned over that
 * slot while the screen is open and hidden (never unmounted) otherwise, so Hubble's own session check
 * happens once, not on every visit. It is torn down when the user logs out or switches identity.
 */

export type HubbleStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface HubbleState {
  status: HubbleStatus;
  src: string;
  error: string;
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface EmbeddedAppsContextValue {
  hubble: HubbleState;
  register: (el: HTMLElement) => void;
  unregister: (el: HTMLElement) => void;
  reload: () => void;
}

const IDLE: HubbleState = { status: 'idle', src: '', error: '' };
// Let the burst of post-login requests settle before spending a request on Hubble's auth.
const PRELOAD_DELAY_MS = 400;
// Hubble tokens expire 60s after issue. Never hand the SDK one with less than this left, and refetch this early.
const TOKEN_LIFETIME_MS = 60_000;
const TOKEN_MIN_REMAINING_MS = 15_000;
const TOKEN_RETRY_MS = 10_000;
// An SDK error while it loads (usually a token that expired in transit) is retried this many times before it's shown.
const MAX_SILENT_RETRIES = 1;

interface CachedToken {
  value: string;
  expiresAt: number;
}

const EmbeddedAppsContext = createContext<EmbeddedAppsContextValue | null>(null);

const getHubbleToken = (response: any): string | undefined =>
  response?.token || response?.data?.token || response?.sso_token || response?.data?.sso_token;

// Lifetime from the JWT's own iat/exp (falls back to 60s), measured from when the request was sent so a slow
// response or a skewed device clock can't make it look fresher than it is.
const getTokenExpiry = (token: string, requestedAt: number) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    const lifetime = (payload.exp - payload.iat) * 1000;
    if (lifetime > 0) return requestedAt + lifetime;
  } catch (e) {
    // Not a decodable JWT; assume the documented lifetime.
  }
  return requestedAt + TOKEN_LIFETIME_MS;
};

const isUsable = (token: CachedToken | null): token is CachedToken =>
  !!token && token.expiresAt - Date.now() > TOKEN_MIN_REMAINING_MS;

const getHubbleOrigin = () => {
  try {
    return new URL(HUBBLE_SDK_URL).origin;
  } catch (e) {
    return '';
  }
};

const sameRect = (a: Rect | null, b: Rect) =>
  !!a && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;

export const EmbeddedAppsProvider: React.FC = ({ children }) => {
  const navigate = useNavigate(),
    auth = useSelector((state: any) => state.auth),
    userDetails = useSelector((state: any) => state.data?.userDetails),
    hasGiftCards = useSelector((state: any) => state.data?.hasGiftCards),
    [hubble, setHubbleState] = useState<HubbleState>(IDLE),
    [slotEl, setSlotEl] = useState<HTMLElement | null>(null),
    [rect, setRect] = useState<Rect | null>(null),
    // Mirrors of state for guards that must see the latest value synchronously (e.g. two effects racing to load).
    hubbleRef = useRef(hubble),
    slotElRef = useRef<HTMLElement | null>(null),
    controllerRef = useRef<AbortController | null>(null),
    tokenRef = useRef<CachedToken | null>(null),
    tokenRequestRef = useRef<Promise<CachedToken> | null>(null),
    tokenGenerationRef = useRef(0),
    tokenTimerRef = useRef<number | undefined>(undefined),
    keepTokenWarmRef = useRef(false),
    silentRetriesRef = useRef(0);

  slotElRef.current = slotEl;

  const identityKey = auth?.isLoggedIn && auth?.member_id ? `${auth.member_id}|${auth.organisation_uuid}` : '',
    // Mirrors the Router: /gift-cards only exists for ACTIVE members in employee mode.
    canPreload = auth?.status === 'ACTIVE' && auth?.activeMode === 'employee' && hasGiftCards === true,
    dataLoaded = !!identityKey && !!userDetails && hasGiftCards !== null;

  keepTokenWarmRef.current = dataLoaded && canPreload;

  const setHubble = useCallback((state: HubbleState) => {
    hubbleRef.current = state;
    setHubbleState(state);
  }, []);

  const clearTokenTimer = () => {
    window.clearTimeout(tokenTimerRef.current);
    tokenTimerRef.current = undefined;
  };

  // Fetches a token into the cache (deduplicating concurrent calls) and schedules the next refresh.
  const prefetchToken = useCallback((): Promise<CachedToken> => {
    if (tokenRequestRef.current) return tokenRequestRef.current;

    clearTokenTimer();
    const generation = tokenGenerationRef.current,
      requestedAt = Date.now();
    const request = api.hubble.createSsoToken().then((response: any) => {
      const value = getHubbleToken(response);
      if (!value) throw new Error('The Hubble token service returned an invalid response.');
      return { value, expiresAt: getTokenExpiry(value, requestedAt) };
    });
    tokenRequestRef.current = request;

    request.then(
      (token) => {
        // Logged out or switched identity while this was in flight: the token belongs to someone else.
        if (generation !== tokenGenerationRef.current) return;
        tokenRequestRef.current = null;
        tokenRef.current = token;
        scheduleRefresh(token.expiresAt - TOKEN_MIN_REMAINING_MS - Date.now());
      },
      () => {
        if (generation !== tokenGenerationRef.current) return;
        tokenRequestRef.current = null;
        scheduleRefresh(TOKEN_RETRY_MS);
      }
    );
    return request;
    // scheduleRefresh only touches refs; it is declared below because the two call each other.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keeps a usable token cached while the user can open Gift Cards and the page is visible. In the background the
  // refresh is skipped; the visibility effect below refetches when the page comes back.
  const scheduleRefresh = (delay: number) => {
    clearTokenTimer();
    tokenTimerRef.current = window.setTimeout(() => {
      tokenTimerRef.current = undefined;
      if (keepTokenWarmRef.current && document.visibilityState !== 'hidden') prefetchToken().catch(() => undefined);
    }, Math.max(delay, 0));
  };

  // Tokens are single-use: take the cached one (or wait for a fresh one) and start fetching its replacement.
  const takeToken = useCallback(async (): Promise<string> => {
    const cached = tokenRef.current;
    const token = isUsable(cached) ? cached : await prefetchToken();
    if (tokenRef.current === token) tokenRef.current = null;
    if (keepTokenWarmRef.current) prefetchToken().catch(() => undefined);
    return token.value;
  }, [prefetchToken]);

  const load = useCallback(async () => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setHubble({ status: 'loading', src: '', error: '' });

    try {
      const token = await takeToken();
      if (controller.signal.aborted) return;

      const url = new URL(HUBBLE_SDK_URL);
      url.searchParams.set('clientId', HUBBLE_CLIENT_ID);
      url.searchParams.set('appSecret', HUBBLE_APP_SECRET);
      url.searchParams.set('token', token);
      // Stays 'loading' until the SDK's app_ready message arrives.
      setHubble({ status: 'loading', src: url.toString(), error: '' });
    } catch (error: any) {
      if (axios.isCancel(error) || controller.signal.aborted) return;

      setHubble({
        status: 'error',
        src: '',
        error:
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          error?.message ||
          'We could not open Gift Cards right now. Please try again.',
      });
      trackData('Hubble Load Failed', { status: error?.response?.status });
    }
  }, [setHubble, takeToken]);

  // A user-initiated retry gets the full silent-retry budget again.
  const reload = useCallback(() => {
    silentRetriesRef.current = 0;
    load();
  }, [load]);

  const register = useCallback((el: HTMLElement) => setSlotEl(el), []);
  const unregister = useCallback((el: HTMLElement) => setSlotEl((current) => (current === el ? null : current)), []);

  // Logout or a different user/organisation: drop the iframe and any token that belongs to the old identity.
  useEffect(
    () => () => {
      controllerRef.current?.abort();
      clearTokenTimer();
      tokenGenerationRef.current += 1;
      tokenRef.current = null;
      tokenRequestRef.current = null;
      silentRetriesRef.current = 0;
      setHubble(IDLE);
    },
    [identityKey, setHubble]
  );

  // Warm the token cache as soon as the user is eligible, and again whenever the page comes back to the
  // foreground with a stale one (timers don't run reliably while a tab or the native app is in the background).
  useEffect(() => {
    if (!dataLoaded || !canPreload) return;

    const refreshIfStale = () => {
      if (document.visibilityState === 'hidden' || isUsable(tokenRef.current)) return;
      prefetchToken().catch(() => undefined);
    };

    refreshIfStale();
    document.addEventListener('visibilitychange', refreshIfStale);
    window.addEventListener('focus', refreshIfStale);
    return () => {
      document.removeEventListener('visibilitychange', refreshIfStale);
      window.removeEventListener('focus', refreshIfStale);
    };
  }, [dataLoaded, canPreload, identityKey, prefetchToken]);

  // Start connecting to Hubble's host early so the iframe's first request doesn't pay for DNS/TLS.
  useEffect(() => {
    const origin = getHubbleOrigin();
    if (!origin || document.querySelector(`link[rel="preconnect"][href="${origin}"]`)) return;

    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = origin;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  }, []);

  // Preload in the background once the post-login data has settled.
  useEffect(() => {
    if (!dataLoaded || !canPreload) return;

    const timer = window.setTimeout(() => {
      if (hubbleRef.current.status === 'idle') load();
    }, PRELOAD_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [dataLoaded, canPreload, hubble.status, load]);

  // The screen was opened before the preload ran (deep link, or still within the delay above).
  useEffect(() => {
    if (slotEl && identityKey && hubbleRef.current.status === 'idle') load();
  }, [slotEl, identityKey, hubble.status, load]);

  // Opening the screen after a background load failed: try again with a fresh token instead of showing a stale error.
  useEffect(() => {
    if (slotEl && identityKey && hubbleRef.current.status === 'error') reload();
  }, [slotEl, identityKey, reload]);

  useEffect(() => {
    const hubbleOrigin = getHubbleOrigin();

    const handleMessage = (event: MessageEvent) => {
      if (!hubbleOrigin || event.origin !== hubbleOrigin || !event.data || typeof event.data !== 'object') return;

      if (event.data.type === 'action') {
        if (event.data.action === 'app_ready') {
          silentRetriesRef.current = 0;
          setHubble({ ...hubbleRef.current, status: 'ready' });
          trackData('Hubble Ready', { Preloaded: !slotElRef.current });
        } else if (event.data.action === 'close') {
          trackData('Hubble Closed');
          navigate('/home');
          // The SDK has ended its session; start a fresh one in the background for the next visit.
          load();
        } else if (event.data.action === 'error') {
          trackData('Hubble SDK Error', { Retry: silentRetriesRef.current });
          // Most load errors are an SSO token that expired before the SDK used it; retry once with a fresh one.
          if (silentRetriesRef.current < MAX_SILENT_RETRIES) {
            silentRetriesRef.current += 1;
            load();
          } else {
            setHubble({ status: 'error', src: '', error: 'Hubble could not start your rewards session. Please try again.' });
          }
        }
      }

      if (event.data.type === 'analytics' && event.data.event) {
        trackData(`Hubble ${event.data.event}`, event.data.properties || {});
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [navigate, load, setHubble]);

  // Follow the screen's slot with the fixed-position iframe.
  useEffect(() => {
    if (!slotEl) {
      setRect(null);
      return;
    }

    const update = () => {
      const { top, left, width, height } = slotEl.getBoundingClientRect();
      const next = { top, left, width, height };
      setRect((current) => (sameRect(current, next) ? current : next));
    };

    update();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    observer?.observe(slotEl);
    // Layout inputs such as the footer height are published after mount, so measure again once they settle.
    const frame = window.requestAnimationFrame(update);
    const timer = window.setTimeout(update, 300);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    return () => {
      observer?.disconnect();
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [slotEl]);

  const value = useMemo(() => ({ hubble, register, unregister, reload }), [hubble, register, unregister, reload]);
  const isShown = !!slotEl && !!rect;

  return (
    <EmbeddedAppsContext.Provider value={value}>
      {children}
      {hubble.src && (
        <iframe
          className={`embedded-app-frame${isShown ? '' : ' is-hidden'}${hubble.status === 'ready' ? ' is-ready' : ''}`}
          style={isShown && rect ? rect : undefined}
          title="Gift Cards"
          src={hubble.src}
          allow="clipboard-write *"
          scrolling="yes"
          aria-hidden={!isShown}
          tabIndex={isShown ? undefined : -1}
        />
      )}
    </EmbeddedAppsContext.Provider>
  );
};

/**
 * For the Gift Cards screen. Attach `slotRef` to the element Hubble should fill; the shared iframe is shown
 * over it while the screen is mounted and hidden again when it unmounts.
 */
export const useHubble = () => {
  const context = useContext(EmbeddedAppsContext);
  if (!context) throw new Error('useHubble must be used inside EmbeddedAppsProvider');

  const { hubble, register, unregister, reload } = context,
    slotRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = slotRef.current;
    if (!el) return;

    register(el);
    return () => unregister(el);
  }, [register, unregister]);

  return { state: hubble, slotRef, reload };
};
